import { useCallback, useEffect, useState } from "react";
import { Select, Button, Drawer, Form, Input } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { DragEndEvent } from "@dnd-kit/core";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, verticalListSortingStrategy,} from "@dnd-kit/sortable";
import { sortColumn } from "../constants";
import DraggableTableRow from "../components/DraggableTableRow";
import requiredRule from "../../../utils/formRules/requiredRule";
import Spinner from "../../../components/layout/spinner/Spinner";
import DrawerFooter from "../../../components/layout/drawer/DrawerFooter";
import MarketDropdown from "../../../components/ui/MarketDropdown";
import TableCustom from "../../../components/ui/TableCustom";
import { TagDataType, TagTabProps } from "./types";

import { useCreateTagMutation } from "../../../graphql/operations/trialMode/testimonial/create-tag";
import { useUpdateTagMutation } from "../../../graphql/operations/trialMode/testimonial/update-tag";
import { useDeleteTagMutation } from "../../../graphql/operations/trialMode/testimonial/delete-tag";
import { useUpdateTestimonialTagSortOrderMutation } from "../../../graphql/operations/trialMode/testimonial/update-tag-sort-id";

import { PlusOutlined } from "@ant-design/icons";
import "./TagTab.scss";

const TagTab = ({
  tags,
  setTags,
  videos,
  market,
  setMarket,
  currentUser,
  refetch,
  category,
}: TagTabProps) => {
  const [total, setTotal] = useState(0);
  const [displayData, setDisplayData] = useState<TagDataType[]>([]);
  const [selectedTag, setSelectedTag] = useState<TagDataType | null>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDragged, setIsDragged] = useState(false);

  const [createTag] = useCreateTagMutation();
  const [updateTag] = useUpdateTagMutation();
  const [deleteTag] = useDeleteTagMutation();
  const [updateSortId, { loading: loadingOfUpdateSortId }] = useUpdateTestimonialTagSortOrderMutation();

  const [form] = Form.useForm();

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setIsDragged(true);
      setTags((previous:TagDataType[]) => {
        const activeIndex = previous.findIndex((i) => i.tagId === active.id);
        const overIndex = previous.findIndex((i) => i.tagId === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
    }
  };

  const handleSubmit = () => {
    const { tagName } = form.getFieldsValue();
    const payload = {
        tagId: selectedTag?.tagId ?? -1,
        tagName: tagName,
        market: market,
    };
    const callback = {
      success: () => {
        setDrawerOpen(false);
        setIsEditMode(false);
        setSelectedTag(null);
      }
    }

    handleMutation(isEditMode ? updateTag : createTag, payload, callback);
  };

  const handleDelete = async (tagId: number) => {
    const payload = {
        tagId: tagId,
    };
    handleMutation(deleteTag, payload);
  };

  const handleMutation = useCallback(
    (
      mutationFn: (arg: any) => Promise<any>,
      variables: any,
      callback?: {
        success?: () => void;
        error?: (e: any) => void;
        finally?: () => void;
      }
    ) => {
      mutationFn({ variables })
        .then(refetch)
        .then(() => {
          callback?.success?.();
        })
        .catch((e: any) => {
          console.error(e);
          callback?.error?.(e);
        })
        .finally(() => {
          callback?.finally?.();
        });
    },
    [refetch]
  );

  const columns: ColumnsType<TagDataType> = [
    sortColumn,
    {
      title: "Title",
      dataIndex: "tagName",
      key: "tagName",
      width: 300,
    },
    {
      title: "Number of videos",
      dataIndex: "numberOfVideos",
      key: "numberOfVideos",
      width: 160,
      render: (number, _) => {
        return number || 0;
      },
    },
    {
      title: "Status",
      dataIndex: "isUsed",
      key: "isUsed",
      width: 115,
      render: (bool, item) => {
        return bool ? (
          <div className="in-use-container">
            <div className="in-use dot" />
            <p>In use</p>
          </div>
        ) : (
          <div className="in-use-container">
            <div className="not-in-use dot" />
            <p>Not in use</p>
          </div>
        );
      },
    },
    {
      title: "Action",
      width: 115,
      render: (_, item) => {
        return (
          <>
            <Button
              type="link"
              onClick={() => {
                setIsEditMode(true);
                setDrawerOpen(true);
                setSelectedTag(item);
              }}
              className="edit-button"
            >
              Edit
            </Button>
            {(item.numberOfVideos === 0 || !item.numberOfVideos) && (
              <Button
                type="link"
                onClick={() => {
                  handleDelete(item.tagId);
                }}
                className="delete-button"
              >
                Delete
              </Button>
            )}
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (tags) {
      setDisplayData(tags);
      setTotal(tags.length);
      if (isDragged) {
        const payload = { tagIds: tags.map((i: TagDataType) => i.tagId) };
        const callback = {
          finally: () => {
              setIsDragged(false)
          }
        }
        handleMutation(updateSortId, payload, callback);
      }
    }
  }, [tags, updateSortId, isDragged, handleMutation]);

  useEffect(() => {
    if (selectedTag) {
      form.setFieldValue("tagName", selectedTag.tagName);
    } else {
      form.resetFields();
      }
  }, [selectedTag, form]);

  if (loadingOfUpdateSortId) return <Spinner />;

  return (
    <>
      <div id="TrialModeTagTabWrapper">
        <div className="table-container">
          <div className="select-total">
            <div className="total-container">Total {total} items</div>
            <div>
              <MarketDropdown
                selectedMarket={market}
                setSelectedMarket={setMarket}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="create-button"
                onClick={() => {
                  setDrawerOpen(true);
                }}
              >
                Create New
              </Button>
            </div>
          </div>
          <div className="table-container no-pt">
          <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
            <SortableContext
              items={tags.map((i:TagDataType) => i.tagId)}
              strategy={verticalListSortingStrategy}
            >
              <TableCustom
                dataSource={displayData}
                columns={columns}
                pagination={false}
                components={{
                  body: {
                    row: DraggableTableRow,
                  },
                }}
                rowKey="tagId"
              />
            </SortableContext>
          </DndContext>
          </div>
        </div>
      </div>
      <Drawer
        open={drawerOpen}
        width={500}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedTag(null);
          setIsEditMode(false);
        }}
        title={isEditMode ? "Edit tag" : "Create new tag"}
        footer={
          <DrawerFooter
            handleSubmit={() => {
              form
                .validateFields()
                .then(() => {
                  handleSubmit();
                })
                .catch((errorInfo) => {
                  console.error("Validation Failed:", errorInfo);
                });
            }}
            handleCancel={() => {
              setDrawerOpen(false);
              setSelectedTag(null);
              setIsEditMode(false);
            }}
          />
        }
      >
        <div id="tagDrawerWrapper">
          <div>
            <p className="title">Market</p>
            <p className="content">{market}</p>
          </div>
          <Form form={form} layout="vertical">
            <Form.Item
              className="form-item"
              label="Tag"
              rules={[requiredRule]}
              name="tagName"
            >
              <Input />
            </Form.Item>
          </Form>
        </div>
      </Drawer>
    </>
  );
};

export default TagTab;
